import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { intlMessage, tagsPropTypes } from 'constants/propTypes';
import { useFormContext } from 'react-hook-form';
import { bem } from 'helpers/class-name';
import { Card, CardTitle } from 'components/Card';
import Message from 'components/Message';
import Prompt from 'components/Prompt';
import SaveBar from 'components/SaveBar';
import { Title } from 'features/App';
import { Tags, Tag, Form, Switch, PhoneNumberInput } from 'components/HookForm';
import LoadingDots from 'components/LoadingDots';
import appMessages from 'features/App/App.messages';
import messages, { notificationTypeMessages } from './Notifications.messages';
import { NOTIFICATION_TYPE, NOTIFICATION_TYPE_LIST } from './Notifications.constants';
import './Notifications.scss';

const FORM_NAME = 'NOTIFICATIONS_FORM';
const { block, element } = bem('notifications');

const PhoneNumberInputSection = ({ disabled, required }) => (
  <div className={element('phone-input-section')}>
    <Message as="label" className="text--light pb-s" message={messages.phoneNumberInput} htmlFor="userPhoneNumber" />
    <PhoneNumberInput
      name="userPhoneNumber"
      id="userPhoneNumber"
      className="mv-s"
      countries={['US']}
      addInternationalOption={false}
      countryCode="US"
      disabled={disabled}
      required={required}
    />
  </div>
);

PhoneNumberInputSection.propTypes = {
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

const NotificationGroup = ({
  notificationFieldStyle,
  notificationTreeName,
  notificationMessage,
  tree,
  type,
  showTitle,
  shouldShowPhoneInput,
  validate,
  disabled,
  required,
}) => {
  const { setValue, watch } = useFormContext();
  const allVenueIds = tree.map(({ id }) => id);
  const currentValues = watch(`receiveNotifications.${type}`);
  const isChecked = !!allVenueIds.every(venueId => currentValues.includes(venueId));

  const handleTreeAllSelected = useCallback(
    event => {
      return event.target.checked
        ? setValue(notificationTreeName, [...allVenueIds], { shouldDirty: true })
        : setValue(notificationTreeName, [], { shouldDirty: true });
    },
    [notificationTreeName, allVenueIds, setValue]
  );

  return (
    <>
      <Card className={notificationFieldStyle}>
        {showTitle && <Message as={CardTitle} message={notificationMessage.title} />}
        <Message as="p" className="text--light" message={notificationMessage.description} />
        <label className={block({ disabled })}>
          <input
            name="treeAllSelected"
            className={element('select-all', { disabled })}
            type="checkbox"
            checked={isChecked}
            onChange={handleTreeAllSelected}
            disabled={disabled}
          />
          <Message message={messages.selectAll} className={element('label', { disabled })} />
        </label>
        <Tags
          name={notificationTreeName}
          className={element('select-notifications')}
          validate={validate}
          tags={tree}
          onChange={(value, checked, activeTagIds = []) =>
            checked ? activeTagIds.concat(value) : activeTagIds.filter(venueId => venueId !== value)
          }>
          {({ id, name }) => <Tag name={id} value={id} label={name} disabled={disabled} />}
        </Tags>
        {shouldShowPhoneInput && <PhoneNumberInputSection disabled={disabled} required={required} />}
      </Card>
    </>
  );
};

NotificationGroup.propTypes = {
  notificationFieldStyle: PropTypes.string,
  notificationTreeName: PropTypes.string,
  notificationMessage: PropTypes.shape({
    title: intlMessage,
    description: intlMessage,
  }),
  tree: tagsPropTypes,
  type: PropTypes.oneOf(NOTIFICATION_TYPE_LIST),
  showTitle: PropTypes.bool,
  shouldShowPhoneInput: PropTypes.bool,
  validate: PropTypes.shape({
    message: intlMessage,
    value: PropTypes.func,
  }),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

const Notifications = ({
  isLoading,
  notificationTypes,
  tagTrees,
  receiveNotifications,
  userSmsSubscriptionStatus,
  userPhoneNumber,
  handleSubmit,
}) => {
  if (isLoading) return <LoadingDots />;
  return (
    <Form
      id={FORM_NAME}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      defaultValues={{
        userSmsSubscriptionStatus,
        userPhoneNumber,
        receiveNotifications,
      }}>
      {({ watch, formState }) => {
        const { isDirty, isSubmitting } = formState;
        const isSmsEnabled = watch('userSmsSubscriptionStatus');

        return (
          <>
            <Prompt when={isDirty} />
            <Message as={Title} message={messages.title} />
            {notificationTypes.map(type => {
              if (type === NOTIFICATION_TYPE.CAN_RECEIVE_SMS) {
                return (
                  <Card key={type}>
                    <Message as={CardTitle} message={notificationTypeMessages[type].title} />
                    <div className={element('toggle-sms')}>
                      <Switch name="userSmsSubscriptionStatus" id="userSmsSubscriptionStatus">
                        {() => (
                          <Message
                            as="label"
                            className="ml-s"
                            message={appMessages.enable}
                            htmlFor="userSmsSubscriptionStatus"
                          />
                        )}
                      </Switch>
                    </div>
                    <Message as="p" className="text--dark" message={notificationTypeMessages[type].disclaimer} />
                    {(isSmsEnabled || receiveNotifications.canReceiveSms.length > 0 || userPhoneNumber) && (
                      <>
                        <NotificationGroup
                          key={type}
                          notificationFieldStyle={element('sms-notification-group')}
                          type={type}
                          notificationTreeName={`receiveNotifications.${type}`}
                          tree={tagTrees[type]}
                          notificationMessage={notificationTypeMessages[type]}
                          disabled={!isSmsEnabled}
                          required={isSmsEnabled}
                          validate={{
                            message: messages.requiredTag,
                            value: value => (isSmsEnabled ? value.length > 0 : true),
                          }}
                          shouldShowPhoneInput
                        />
                      </>
                    )}
                  </Card>
                );
              }
              return (
                <NotificationGroup
                  key={type}
                  type={type}
                  notificationTreeName={`receiveNotifications.${type}`}
                  tree={tagTrees[type]}
                  notificationMessage={notificationTypeMessages[type]}
                  showTitle
                />
              );
            })}
            <SaveBar visible={isDirty} submitting={isSubmitting} formName={FORM_NAME} />
          </>
        );
      }}
    </Form>
  );
};

Notifications.propTypes = {
  isLoading: PropTypes.bool,
  notificationTypes: PropTypes.arrayOf(PropTypes.oneOf(NOTIFICATION_TYPE_LIST)),
  tagTrees: PropTypes.shape(NOTIFICATION_TYPE_LIST.reduce((memo, type) => ({ ...memo, [type]: tagsPropTypes }), {})),
  receiveNotifications: PropTypes.shape(
    NOTIFICATION_TYPE_LIST.reduce((memo, type) => ({ ...memo, [type]: PropTypes.arrayOf(PropTypes.string) }), {})
  ),
  userSmsSubscriptionStatus: PropTypes.bool,
  userPhoneNumber: PropTypes.string,
  handleSubmit: PropTypes.func,
};

export default Notifications;
